import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Text } from '@zillow/constellation-10';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useGetListingDetails } from '../../../hooks/queries/properties';
import { useFeedOverrideSectionEnabled } from '../../../hooks/useFeedOverrideSectionEnabled';
import accessUtils from '../../../utils/accessUtils';

import FeedPhotoUploadPausedMessage from '../../PostingPath/MediaContent/PhotoUploadFormField/components/FeedPhotoUploadPausedMessage';
import PhotoGallery from '../../PostingPath/MediaContent/PhotoUploadFormField/components/PhotoGallery';
import PhotoUpload from '../../PostingPath/MediaContent/PhotoUploadFormField/components/PhotoUpload';

type Props = {
  floorPlanIndex: number;
  /** Feed identity captured when the modal opened; the list can re-sort on refetch, so the index alone can drift. */
  feedModelId?: string;
};

const FloorPlanUploadFormField = ({ floorPlanIndex: initialFloorPlanIndex, feedModelId }: Props) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;

  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [uploadingCount, setUploadingCount] = useState(0);

  const isFeed = listingDetails?.isFeed ?? false;
  const feedFloorPlanIndex =
    isFeed && feedModelId
      ? (listingDetails?.floorPlans ?? []).findIndex((plan) => plan.feedModelId === feedModelId)
      : -1;
  const floorPlanIndex = feedFloorPlanIndex === -1 ? initialFloorPlanIndex : feedFloorPlanIndex;
  const floorPlan = listingDetails?.floorPlans?.[floorPlanIndex];
  const floorPlanPhotos = floorPlan?.floorPlanPhotos || [];

  const isFloorPlansOverrideEnabled = useFeedOverrideSectionEnabled(listingAlias, FeedOverrideSection.FLOOR_PLANS);
  const isUploadAllowed = !isFeed || (isFloorPlansOverrideEnabled && accessUtils.canAccessFeedOverrides());

  if (!floorPlanPhotos.length && !isUploadAllowed) {
    return (
      <Box data-testid="FloorPlanUploadFormField" css={{ width: '100%' }}>
        <Text css={{ marginTop: '200', flex: '1 0 fit-content' }}>No photos available</Text>
      </Box>
    );
  }

  return (
    <Box data-testid="FloorPlanUploadFormField" css={{ width: '100%' }}>
      {floorPlanPhotos.length > 0 || uploadingCount > 0 ? (
        <PhotoGallery
          photoContext={{ type: 'floorPlan', floorPlanId: floorPlan?.id || '', floorPlanIndex }}
          uploadingCount={uploadingCount}
          setUploadingCount={setUploadingCount}
          isUploadAllowed={isUploadAllowed}
        />
      ) : (
        <>
          {isUploadAllowed && (
            <>
              <FeedPhotoUploadPausedMessage
                listingAlias={listingAlias}
                scope={{ type: 'floorPlan', floorPlanId: floorPlan?.id || '' }}
              />
              <PhotoUpload
                photoContext={{ type: 'floorPlan', floorPlanId: floorPlan?.id || '', floorPlanIndex }}
                setUploadingCount={setUploadingCount}
                uploadingCount={uploadingCount}
              />
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default memo(FloorPlanUploadFormField);
